import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Search, MapPin, LayoutGrid, List, Filter } from 'lucide-react';
import { entriesApi, gachasApi, upgradesApi, gachaIslandsApi, upgradeIslandsApi, achievementsApi } from '../lib/api';
import { WikiGrid } from '../components/wiki/WikiGrid';
import { WikiDetail } from '../components/wiki/WikiDetail';
import { Input } from '../components/ui/input';
import { useSiteLock } from '../context/SiteLockContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useView } from '../context/ViewContext';
import { cn } from '../lib/utils';

const CATEGORY_TITLES = {
    relics: { title: 'Relics', description: 'Entdecke mächtige Artefakte und ihre Fähigkeiten', descriptionEn: 'Discover powerful artifacts and their abilities' },
    accessories: { title: 'Accessories', description: 'Entdecke mächtige Accessoires und ihre Eigenschaften', descriptionEn: 'Discover powerful accessories and their properties' },
    skills: { title: 'Skills', description: 'Entdecke mächtige Skills und ihre Fähigkeiten', descriptionEn: 'Discover powerful skills and their abilities' },
    gachas: { title: 'Gachas', description: 'Erfahre mehr über Gacha-Mechaniken und Drop-Raten', descriptionEn: 'Learn more about gacha mechanics and drop rates' },
    upgrades: { title: 'Upgrades', description: 'Alle Upgrade-Pfade und Anforderungen', descriptionEn: 'All upgrade paths and requirements' },
    achievements: { title: 'Achievements', description: 'Verfolge deinen Fortschritt und schalte Belohnungen frei', descriptionEn: 'Track your progress and unlock rewards' },
    tutorials: { title: 'Tutorials', description: 'Schritt-für-Schritt Anleitungen für deinen Erfolg', descriptionEn: 'Step-by-step guides for your success' },
};

const RARITY_FILTER_OPTIONS = [
    { id: 'all', name: 'All', color: 'text-gray-400' },
    { id: 'Common', name: 'Common', color: 'text-gray-400' },
    { id: 'Uncommon', name: 'Uncommon', color: 'text-green-400' },
    { id: 'Rare', name: 'Rare', color: 'text-blue-400' },
    { id: 'Epic', name: 'Epic', color: 'text-purple-400' },
    { id: 'Legendary', name: 'Legendary', color: 'text-amber-400' },
    { id: 'Mythical', name: 'Mythical', color: 'text-red-400' },
    { id: 'Monarch', name: 'Monarch', color: 'text-sky-400' },
    { id: 'Limited', name: 'Limited', color: 'text-pink-400' },
    { id: 'ULimited', name: 'ULimited', color: 'text-violet-400' },
];

export default function WikiPage() {
    const { id } = useParams();
    const location = useLocation();
    const { isPageLocked } = useSiteLock();
    const { isAdmin } = useAuth();
    const { language, t } = useLanguage();
    const { isGrid, toggleView } = useView();
    
    // Extract category from pathname
    const pathParts = location.pathname.split('/').filter(Boolean);
    const category = pathParts[0];
    
    const [entries, setEntries] = useState([]);
    const [islands, setIslands] = useState([]);
    const [selectedIsland, setSelectedIsland] = useState(null);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [rarityFilter, setRarityFilter] = useState('all');

    const categoryInfo = CATEGORY_TITLES[category] || { title: category || 'Unknown', description: '', descriptionEn: '' };
    const isIslandBased = category === 'gachas' || category === 'upgrades';
    const isAchievements = category === 'achievements';
    const isLocked = isPageLocked(category);
    const hasRarity = ['relics', 'accessories', 'skills'].includes(category);

    useEffect(() => {
        if (isLocked) return;
        if (id) {
            loadSingleEntry(id);
        } else {
            if (isIslandBased) {
                loadIslands();
            } else if (isAchievements) {
                loadAchievements();
            } else {
                loadEntries();
            }
        }
    }, [category, id, isLocked]);

    useEffect(() => {
        if (isLocked) return;
        if (isIslandBased && selectedIsland) {
            loadIslandEntries(selectedIsland.id);
        }
    }, [selectedIsland, isLocked]);

    // Show locked overlay if page is locked for non-admins
    if (isLocked) {
        return (
            <div className="min-h-[80vh] flex items-center justify-center">
                <div className="text-center p-12 rounded-2xl bg-gradient-to-br from-red-950/50 to-red-900/30 border border-red-500/20 backdrop-blur-sm max-w-md">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center border border-red-500/30">
                        <svg className="w-10 h-10 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-3 font-['Rajdhani'] tracking-wide">{t('lockedForDev')}</h1>
                    <p className="text-gray-400 text-lg mb-2">{t('pageBeingUpdated')}</p>
                    <p className="text-gray-500 text-sm">{t('checkBackLater')}</p>
                </div>
            </div>
        );
    }

    const loadAchievements = async () => {
        setLoading(true);
        try {
            const response = await achievementsApi.getAll();
            const data = response.data;
            setEntries(Array.isArray(data) ? data : (data.items || []));
        } catch (error) {
            console.error('Failed to load achievements:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadIslands = async () => {
        setLoading(true);
        try {
            // Use the correct API based on category
            const api = category === 'gachas' ? gachaIslandsApi : upgradeIslandsApi;
            const response = await api.getAll();
            const data = Array.isArray(response.data) ? response.data : (response.data.items || response.data);
            setIslands(data);
            // Auto-select first island if available
            if (data.length > 0) {
                setSelectedIsland(data[0]);
            } else {
                setLoading(false);
            }
        } catch (error) {
            console.error('Failed to load islands:', error);
            setLoading(false);
        }
    };

    const loadIslandEntries = async (islandId) => {
        setLoading(true);
        try {
            let response;
            if (category === 'gachas') {
                response = await gachasApi.getAll(islandId);
            } else {
                response = await upgradesApi.getAll(islandId);
            }
            const data = response.data;
            setEntries(Array.isArray(data) ? data : (data.items || []));
            setSelectedEntry(null);
        } catch (error) {
            console.error('Failed to load entries:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadEntries = async () => {
        if (!category) return;
        setLoading(true);
        try {
            const response = await entriesApi.getAll(category);
            const data = response.data;
            setEntries(Array.isArray(data) ? data : (data.items || []));
            setSelectedEntry(null);
        } catch (error) {
            console.error('Failed to load entries:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadSingleEntry = async (entryId) => {
        setLoading(true);
        try {
            let response;
            if (category === 'gachas') {
                response = await gachasApi.getById(entryId);
            } else if (category === 'upgrades') {
                response = await upgradesApi.getById(entryId);
            } else if (category === 'achievements') {
                response = await achievementsApi.getById(entryId);
            } else {
                response = await entriesApi.getById(entryId);
            }
            setSelectedEntry(response.data);
        } catch (error) {
            console.error('Failed to load entry:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredEntries = entries.filter(entry => {
        const title = entry.title || entry.name || '';
        const description = entry.description || '';
        const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase()) ||
               description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesRarity = rarityFilter === 'all' || entry.rarity === rarityFilter;
        return matchesSearch && matchesRarity;
    });

    // Normalize entries for WikiGrid (gachas/upgrades use 'name' instead of 'title')
    const normalizedEntries = filteredEntries.map(entry => ({
        ...entry,
        title: entry.title || entry.name,
    }));

    // Detail View
    if (id) {
        return (
            <div data-testid={`${category}-detail-page`}>
                <WikiDetail entry={selectedEntry} category={category} />
            </div>
        );
    }

    const description = language === 'en' ? categoryInfo.descriptionEn : categoryInfo.description;

    // List View
    return (
        <div className="space-y-8" data-testid={`${category}-page`}>
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="space-y-2">
                    <h1 className="text-3xl sm:text-4xl font-bold font-['Rajdhani'] tracking-wider text-white dark:text-white light:text-gray-900">
                        {categoryInfo.title}
                    </h1>
                    <p className="text-gray-400 dark:text-gray-400 light:text-gray-600 text-base sm:text-lg">
                        {description}
                    </p>
                </div>
                {/* View Toggle */}
                <button
                    onClick={toggleView}
                    className="p-2 rounded-lg bg-white/5 dark:bg-white/5 light:bg-gray-100 text-gray-400 hover:text-white transition-colors"
                    title={isGrid ? t('listView') : t('gridView')}
                >
                    {isGrid ? <List size={20} /> : <LayoutGrid size={20} />}
                </button>
            </div>

            {/* Island Selector for Gachas/Upgrades */}
            {isIslandBased && islands.length > 0 && (
                <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                        <MapPin size={14} />
                        {t('selectIslandLabel')}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                        {islands.map((island) => (
                            <button
                                key={island.id}
                                onClick={() => setSelectedIsland(island)}
                                data-testid={`island-filter-${island.id}`}
                                className={cn(
                                    "px-4 py-2 rounded-lg border transition-all text-sm font-medium",
                                    selectedIsland?.id === island.id
                                        ? "bg-cyan-500/20 border-cyan-500/50 text-cyan-400"
                                        : "bg-white/5 border-white/10 text-gray-400 hover:border-white/20 hover:text-white dark:bg-white/5 dark:border-white/10 light:bg-gray-100 light:border-gray-200"
                                )}
                            >
                                {island.name}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Search */}
            <div className="relative max-w-md">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                <Input
                    data-testid="search-input"
                    type="text"
                    placeholder={`${categoryInfo.title.toLowerCase()} ${language === 'de' ? 'durchsuchen' : 'search'}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-12 bg-black/40 dark:bg-black/40 light:bg-white border-white/10 dark:border-white/10 light:border-gray-200 rounded-full h-12"
                />
            </div>

            {/* Rarity Filter - nur für relics, accessories, skills */}
            {hasRarity && (
                <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                        <Filter size={14} />
                        {t('filterByRarity')}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                        {RARITY_FILTER_OPTIONS.map((rarity) => (
                            <button
                                key={rarity.id}
                                onClick={() => setRarityFilter(rarity.id)}
                                data-testid={`rarity-filter-${rarity.id}`}
                                className={cn(
                                    "px-3 py-1.5 rounded-lg border transition-all text-xs font-medium",
                                    rarityFilter === rarity.id
                                        ? `bg-white/10 border-white/30 ${rarity.color}`
                                        : "bg-white/5 border-white/10 text-gray-500 hover:border-white/20 hover:text-gray-300"
                                )}
                            >
                                {rarity.name}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* No Islands Message */}
            {isIslandBased && islands.length === 0 && !loading && (
                <div className="p-12 text-center border border-dashed border-white/10 dark:border-white/10 light:border-gray-200 rounded-xl">
                    <MapPin size={48} className="mx-auto mb-4 text-gray-600" />
                    <p className="text-gray-400">{t('noIslandsYet')}</p>
                    <p className="text-gray-600 text-sm mt-2">{t('adminsCanCreateIslands')}</p>
                </div>
            )}

            {/* Content Grid */}
            {(!isIslandBased || selectedIsland) && (
                <WikiGrid 
                    entries={normalizedEntries} 
                    category={category} 
                    loading={loading}
                    viewMode={isGrid ? 'grid' : 'list'}
                />
            )}
        </div>
    );
}
